<template>
  <div class="header-footer-editor">
    <!-- Page Actions Bar -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-2">
        <a
          v-if="previewUrl"
          :href="previewUrl"
          target="_blank"
          class="btn btn-link btn-arrow text-body-sm">
          Preview site
        </a>
      </div>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="isSaving"
          @click="handleSaveAll">
          <svg
            v-if="isSaving"
            class="w-4 h-4 mr-1 animate-spin"
            fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Save Changes
        </button>
        <button
          v-if="canDeploy"
          type="button"
          class="btn btn-secondary btn-sm"
          :disabled="isDeploying"
          @click="handleDeploy">
          <svg
            v-if="isDeploying"
            class="w-4 h-4 mr-1 animate-spin"
            fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <svg
            v-else
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Deploy Header/Footer
        </button>
      </div>
    </div>

    <!-- Live Preview Section -->
    <div class="card mb-6">
      <div class="card-header">
        <div class="flex items-center justify-between w-full">
          <div>
            <h2 class="card-title flex items-center gap-2">
              <svg class="w-5 h-5" style="color: var(--text-tertiary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Live Preview
            </h2>
            <p class="card-description">
              See your changes in real-time before saving
            </p>
          </div>
          <div class="flex items-center gap-2">
            <!-- Desktop/Mobile toggle -->
            <div class="tabs-pills">
              <button
                type="button"
                :class="['tab-pill', { active: previewMode === 'desktop' }]"
                @click="previewMode = 'desktop'">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Desktop
              </button>
              <button
                type="button"
                :class="['tab-pill', { active: previewMode === 'mobile' }]"
                @click="previewMode = 'mobile'">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Mobile
              </button>
            </div>
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-icon"
              title="Refresh preview"
              @click="refreshPreview">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="card-body p-0">
        <PreviewPane
          ref="previewPane"
          :header-content="headerContent"
          :footer-content="footerContent"
          :header-settings="headerSettings"
          :footer-settings="footerSettings"
          :header-template="headerTemplate"
          :footer-template="footerTemplate"
          :preview-mode="previewMode"
          :api-endpoints="apiEndpoints" />
      </div>
    </div>

    <!-- Editor Panels Grid -->
    <div class="grid lg:grid-cols-2 gap-6">
      <!-- Header Editor -->
      <HeaderEditor
        v-model:content="headerContent"
        v-model:settings="headerSettings"
        v-model:template="headerTemplate"
        :templates="headerTemplates"
        :services="services"
        :service-areas="serviceAreas"
        :business-name="businessName"
        :logo-url="logoUrl"
        @update="handleHeaderUpdate" />

      <!-- Footer Editor -->
      <FooterEditor
        v-model:content="footerContent"
        v-model:settings="footerSettings"
        v-model:template="footerTemplate"
        :templates="footerTemplates"
        :services="services"
        :service-areas="serviceAreas"
        :business-name="businessName"
        :api-endpoints="apiEndpoints"
        @update="handleFooterUpdate" />
    </div>

    <!-- Toast Notifications -->
    <div
      v-if="toast.show"
      class="fixed bottom-4 right-4 z-50 animate-fade-in">
      <div
        :class="[
          'px-4 py-3 rounded-lg shadow-lg flex items-center gap-2',
          toast.type === 'success' ? 'bg-green-600 text-white' : '',
          toast.type === 'error' ? 'bg-red-600 text-white' : '',
          toast.type === 'info' ? 'bg-blue-600 text-white' : ''
        ]">
        <span>{{ toast.message }}</span>
        <button
          type="button"
          class="ml-2 opacity-75 hover:opacity-100"
          @click="toast.show = false">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, provide, watch } from 'vue'
import HeaderEditor from './HeaderEditor.vue'
import FooterEditor from './FooterEditor.vue'
import PreviewPane from './PreviewPane.vue'

// Props from Stimulus controller
const props = defineProps({
  websiteId: { type: String, required: true },
  initialHeaderContent: { type: Object, default: () => ({}) },
  initialFooterContent: { type: Object, default: () => ({}) },
  initialHeaderSettings: { type: Object, default: () => ({}) },
  initialFooterSettings: { type: Object, default: () => ({}) },
  initialHeaderTemplate: { type: String, default: 'navbar_service' },
  initialFooterTemplate: { type: String, default: 'footer_columns' },
  services: { type: Array, default: () => []},
  serviceAreas: { type: Array, default: () => []},
  headerTemplates: { type: Array, default: () => []},
  footerTemplates: { type: Array, default: () => []},
  businessName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  previewUrl: { type: String, default: '' },
  canDeploy: { type: Boolean, default: false },
  apiEndpoints: { type: Object, required: true }
})

// Reactive state
const headerContent = ref({ ...props.initialHeaderContent })
const footerContent = ref({ ...props.initialFooterContent })
const headerSettings = ref({ ...props.initialHeaderSettings })
const footerSettings = ref({ ...props.initialFooterSettings })
const headerTemplate = ref(props.initialHeaderTemplate)
const footerTemplate = ref(props.initialFooterTemplate)

// UI state
const previewMode = ref('desktop')
const isSaving = ref(false)
const isDeploying = ref(false)
const previewPane = ref(null)

// Toast notification state
const toast = reactive({
  show: false,
  message: '',
  type: 'info'
})

// Provide shared state to child components
provide('websiteId', props.websiteId)
provide('apiEndpoints', props.apiEndpoints)
provide('services', props.services)
provide('serviceAreas', props.serviceAreas)
provide('businessName', props.businessName)
provide('logoUrl', props.logoUrl)

// Show toast notification
function showToast(message, type = 'info') {
  toast.message = message
  toast.type = type
  toast.show = true
  setTimeout(() => {
    toast.show = false
  }, 3000)
}

// Debounced preview update
let previewTimeout = null
function schedulePreviewUpdate() {
  if (previewTimeout) {
    clearTimeout(previewTimeout)
  }
  previewTimeout = setTimeout(() => {
    if (previewPane.value) {
      previewPane.value.refresh()
    }
  }, 500)
}

// Handle header updates
function handleHeaderUpdate() {
  schedulePreviewUpdate()
}

// Handle footer updates
function handleFooterUpdate() {
  schedulePreviewUpdate()
}

// Refresh preview manually
function refreshPreview() {
  if (previewPane.value) {
    previewPane.value.refresh()
  }
}

// Save all changes
async function handleSaveAll() {
  isSaving.value = true
  try {
    // Save header
    const headerResponse = await fetch(props.apiEndpoints.update_header, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        header_block_type: headerTemplate.value,
        header_content: headerContent.value,
        header_settings: headerSettings.value
      })
    })

    if (!headerResponse.ok) {
      throw new Error('Failed to save header')
    }

    // Save footer
    const footerResponse = await fetch(props.apiEndpoints.update_footer, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        footer_block_type: footerTemplate.value,
        footer_content: footerContent.value,
        footer_settings: footerSettings.value
      })
    })

    if (!footerResponse.ok) {
      throw new Error('Failed to save footer')
    }

    showToast('Changes saved successfully', 'success')
  } catch (error) {
    console.error('Save error:', error)
    showToast('Failed to save changes', 'error')
  } finally {
    isSaving.value = false
  }
}

// Deploy header/footer to live site
async function handleDeploy() {
  isDeploying.value = true
  try {
    const response = await fetch(props.apiEndpoints.deploy, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })

    if (!response.ok) {
      throw new Error('Failed to start deployment')
    }

    showToast('Deployment started', 'success')
  } catch (error) {
    console.error('Deploy error:', error)
    showToast('Failed to start deployment', 'error')
  } finally {
    isDeploying.value = false
  }
}

// Watch for content changes to trigger preview updates
watch([headerContent, headerSettings, headerTemplate], handleHeaderUpdate, { deep: true })
watch([footerContent, footerSettings, footerTemplate], handleFooterUpdate, { deep: true })
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
